import React, { Fragment } from 'react'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react'
import { EllipsisHorizontalIcon } from '@heroicons/react/20/solid'
import { iconButtonClass } from '../iconButtonClass'

export interface ActionMenuItem {
  label: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon?: React.ComponentType<any>
  onClick?: () => void
  danger?: boolean
  badge?: number | string
  /** Render a divider above this item. */
  dividerBefore?: boolean
  'data-cy'?: string
  'data-testid'?: string
}

interface ActionMenuProps {
  items: ActionMenuItem[]
  label?: string
  'data-testid'?: string
}

// The "…" overflow menu for table row actions. Secondary, status-agnostic actions
// live here so the action column stays uniform; the row's primary action remains a
// labeled button next to it. Anchored (portaled) so the dropdown escapes the
// table's overflow-x-auto clip.
const ActionMenu = ({ items, label = 'Row actions', 'data-testid': testId }: ActionMenuProps) => {
  if (!items.length) return null
  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        onClick={(e) => e.stopPropagation()}
        aria-label={label}
        data-testid={testId}
        className={iconButtonClass}
      >
        <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
      </MenuButton>
      <MenuItems
        anchor="bottom end"
        transition
        className="z-50 mt-1 min-w-48 rounded-lg border border-gray-200 bg-white p-1 shadow-lg focus:outline-hidden transition duration-100 ease-out data-closed:scale-95 data-closed:opacity-0"
      >
        {items.map((item, idx) => (
          <Fragment key={`${item.label}-${idx}`}>
            {item.dividerBefore && <div className="my-1 h-px bg-gray-100" />}
            <MenuItem>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); item.onClick?.() }}
                data-cy={item['data-cy']}
                data-testid={item['data-testid']}
                className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm data-focus:bg-gray-50 ${item.danger ? 'text-cerise-red-dark' : 'text-gray-700'}`}
              >
                {item.icon && (
                  <item.icon className={`h-4 w-4 shrink-0 ${item.danger ? 'text-cerise-red-500' : 'text-gray-400'}`} aria-hidden="true" />
                )}
                <span className="flex-1 whitespace-nowrap text-left">{item.label}</span>
                {item.badge !== undefined && (
                  <span className="ml-2 inline-flex items-center justify-center rounded-full bg-cerise-red px-1.5 text-[10px] font-bold tabular-nums text-white">
                    {item.badge}
                  </span>
                )}
              </button>
            </MenuItem>
          </Fragment>
        ))}
      </MenuItems>
    </Menu>
  )
}

export default ActionMenu
